/**
 * QUIPWA — when the Fichero skin's sounds keep quiet (sound.ts asks; pure, so
 * it is tested without a page)
 *
 *   skin        the flat skin is silent: a flat button does not click
 *   off         the sound is off (it is, until turned on)
 *   volume      at zero
 *   microphone  a microphone is open: it would pick the sound up, and the
 *               transcript would carry it
 *   voice       a voice is reading: nothing over the question
 *   hidden      the page is not on screen
 *
 * The device's own mute is the platform's: the sounds go out at the media
 * volume, and on an iPhone they are «ambient» (fichero-sounds.ts), which the
 * silent switch mutes.
 */
import type { Skin } from '../theme/skin'

export interface SoundGateInput {
  skin: Skin
  on: boolean
  volume: number
  microphones: number
  speaking: boolean
  hidden: boolean
}

export type SoundBlock = 'skin' | 'off' | 'volume' | 'microphone' | 'voice' | 'hidden'

/** Why no sound may play now, or null if one may. */
export function soundBlocked(s: SoundGateInput): SoundBlock | null {
  if (s.skin !== 'fichero') return 'skin'
  if (!s.on) return 'off'
  if (!(s.volume > 0)) return 'volume'
  if (s.microphones > 0) return 'microphone'
  if (s.speaking) return 'voice'
  if (s.hidden) return 'hidden'
  return null
}

/** The same sound again this soon is the same action arriving twice. */
export const ECHO_MS = 80

export function isEcho(last: { name: string; at: number } | null, name: string, at: number, ms = ECHO_MS): boolean {
  return last !== null && last.name === name && at - last.at >= 0 && at - last.at < ms
}

/** Volume (0–1) → gain. By ear, loudness follows roughly the square. */
export function soundGain(volume: number): number {
  const v = Number.isFinite(volume) ? Math.min(1, Math.max(0, volume)) : 0
  return v * v
}
